import { useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Label } from '../ui/Label.js';
import { Select } from '../ui/Select.js';
import {
  useCreateProvider,
  useUpdateProvider,
  type AdminProvider,
  type CreateProviderPayload,
} from '../../hooks/useAdminProviders.js';

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'] as const;

const CURATION_STATES = [
  { value: 'shell', label: 'Shell (imported, not yet reviewed)' },
  { value: 'curated', label: 'Curated (verified, live)' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'archived', label: 'Archived' },
];

type FormValues = {
  name: string;
  shortName: string;
  cricosProviderCode: string;
  rtoCode: string;
  websiteUrl: string;
  primaryState: string;
  contactEmail: string;
  curationState: 'shell' | 'curated' | 'needs_review' | 'archived';
};

type Props = {
  initial?: AdminProvider | null;
};

function toFormValues(p?: AdminProvider | null): FormValues {
  return {
    name: p?.name ?? '',
    shortName: p?.shortName ?? '',
    cricosProviderCode: p?.cricosProviderCode ?? '',
    rtoCode: p?.rtoCode ?? '',
    websiteUrl: p?.websiteUrl ?? '',
    primaryState: p?.primaryState ?? '',
    contactEmail: p?.contactEmail ?? '',
    curationState: p?.curationState ?? 'curated',
  };
}

function toPayload(v: FormValues): CreateProviderPayload {
  return {
    name: v.name.trim(),
    shortName: v.shortName.trim() || null,
    cricosProviderCode: v.cricosProviderCode.trim() || null,
    rtoCode: v.rtoCode.trim() || null,
    websiteUrl: v.websiteUrl.trim() || null,
    primaryState: v.primaryState || null,
    contactEmail: v.contactEmail.trim() || null,
    curationState: v.curationState,
  };
}

export function ProviderForm({ initial }: Props) {
  const navigate = useNavigate();
  const isEdit = !!initial;
  const create = useCreateProvider();
  const update = useUpdateProvider(initial?.id ?? '');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: toFormValues(initial) });

  const onSubmit = handleSubmit(async (values) => {
    const payload = toPayload(values);
    try {
      if (isEdit) {
        await update.mutateAsync(payload);
      } else {
        await create.mutateAsync(payload);
      }
      navigate({ to: '/app/admin/providers' });
    } catch (e) {
      // ApiError is already surfaced by TanStack Query; we just keep the user on the form.
      console.error(e);
    }
  });

  const mutationError =
    (create.error as Error | null)?.message ?? (update.error as Error | null)?.message ?? null;

  return (
    <form onSubmit={onSubmit} className="space-y-8 max-w-3xl">
      {mutationError && (
        <div className="bg-danger-bg border border-danger/30 text-danger rounded-lg px-4 py-3 text-sm">
          {mutationError}
        </div>
      )}

      <section className="space-y-5">
        <h2 className="font-serif text-lg text-ink">Basics</h2>
        <div>
          <Label htmlFor="name">Provider name</Label>
          <Input
            id="name"
            placeholder="e.g. University of Wollongong"
            invalid={!!errors.name}
            {...register('name', { required: 'Provider name is required' })}
          />
          {errors.name && <p className="text-danger text-xs mt-1">{errors.name.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="shortName">Short name</Label>
            <Input id="shortName" placeholder="UoW" {...register('shortName')} />
          </div>
          <div>
            <Label htmlFor="primaryState">Primary state</Label>
            <Select id="primaryState" {...register('primaryState')}>
              <option value="">—</option>
              {AU_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cricosProviderCode">CRICOS provider code</Label>
            <Input
              id="cricosProviderCode"
              placeholder="00102E"
              {...register('cricosProviderCode', {
                pattern: {
                  value: /^$|^\d{5}[A-Z]$/,
                  message: 'Format: 5 digits + uppercase letter',
                },
              })}
              invalid={!!errors.cricosProviderCode}
            />
            {errors.cricosProviderCode && (
              <p className="text-danger text-xs mt-1">{errors.cricosProviderCode.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="rtoCode">RTO code (optional)</Label>
            <Input id="rtoCode" placeholder="90123" {...register('rtoCode')} />
          </div>
        </div>

        <div>
          <Label htmlFor="websiteUrl">Website</Label>
          <Input
            id="websiteUrl"
            type="url"
            placeholder="https://www.uow.edu.au"
            {...register('websiteUrl')}
          />
        </div>

        <div>
          <Label htmlFor="contactEmail">Contact email (optional)</Label>
          <Input
            id="contactEmail"
            type="email"
            placeholder="international@uow.edu.au"
            {...register('contactEmail')}
          />
        </div>
      </section>

      <section className="space-y-5 pt-6 border-t border-line">
        <h2 className="font-serif text-lg text-ink">Curation</h2>
        <div>
          <Label htmlFor="curationState">State</Label>
          <Select id="curationState" {...register('curationState')}>
            {CURATION_STATES.map((cs) => (
              <option key={cs.value} value={cs.value}>
                {cs.label}
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted mt-1.5">
            "Curated" stamps the last-verified date to now.
          </p>
        </div>
      </section>

      <div className="flex items-center gap-3 pt-4 border-t border-line">
        <Button type="submit" loading={isSubmitting}>
          {isEdit ? 'Save changes' : 'Create provider'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate({ to: '/app/admin/providers' })}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
